/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Table, Badge } from 'antd';
import { usePaginationWrapper } from '@hooks/usePaginationWrapper';
import { getCustomNoData } from '@utils/noDataCustom';
import { MESSAGES } from '@translations/labels';

const labels = { ...MESSAGES.manageBot };

const summarizeIssues = (issues = []) => {
  const sourceMap = {};
  const typeMap = {};
  const priorityMap = {};

  issues.forEach(issue => {
    if (!typeMap[issue.type]) {
      typeMap[issue.type] = 1;
    } else {
      typeMap[issue.type] += 1;
    }
    if (!sourceMap[issue.source]) {
      sourceMap[issue.source] = 1;
    } else {
      sourceMap[issue.source] += 1;
    }
    if (!priorityMap[issue.priority]) {
      priorityMap[issue.priority] = 1;
    } else {
      priorityMap[issue.priority] += 1;
    }
  });
  return {
    issuesLength: issues.length,
    sourceSummary: sourceMap,
    typeSummary: typeMap,
    prioritySummary: priorityMap
  };
};

const formatSingleSummary = summary => {
  let txt = ``;
  const levelKeys = Object.keys(summary);
  levelKeys.forEach(levelKey => {
    txt += `, ${levelKey}: ${summary[levelKey]}`;
  });
  return txt;
};

const formatSummary = summary => {
  let txt = `${labels.tableLastTrainingSummaryText}: ${summary.issuesLength}`;

  txt += formatSingleSummary(summary.sourceSummary);
  txt += formatSingleSummary(summary.typeSummary);
  txt += formatSingleSummary(summary.prioritySummary);

  return `(${txt})`;
};

const LastTrainingTable = ({ issues }) => {
  const { paginationWrapper } = usePaginationWrapper();
  const summary = summarizeIssues(issues);
  const formattedSummary = formatSummary(summary);
  const columns = [
    {
      key: 'action',
      align: 'center',
      fixed: true,
      width: '3%',
      render: record => <Badge status={record.type} />
    },
    {
      title: (
        <span className="bold capitalized">
          {labels.errorsDetected} {formattedSummary}
        </span>
      ),
      key: 'action',
      fixed: true,
      width: '70%',
      render: record => {
        let partialSentences = '';
        partialSentences = labels[record.reason];
        const sentences = partialSentences.map(partial => {
          if (partial[0] !== '[') {
            return `<span>${partial}</span>`;
          }
          // eslint-disable-next-line no-param-reassign
          partial = partial.replace('[', '');
          // eslint-disable-next-line no-param-reassign
          partial = partial.replace(']', '');
          const link = `/manage-the-bot/intents/${partial === 'intent1' ? record.intentId1 : record.intentId0}/edit`;
          return ['intent0', 'intent1'].includes(partial)
            ? `<a href=${link}>${record[partial]}</a>`
            : `<strong>"${record[partial]}"</strong>`;
        });
        // eslint-disable-next-line react/no-danger
        return <p dangerouslySetInnerHTML={{ __html: sentences.join('') }} />;
      }
    },
    {
      title: <span className="bold uppercase">{labels.typeError}</span>,
      key: 'action',
      fixed: true,
      width: '12%',
      sorter: (a, b) => {
        return a.source.localeCompare(b.source);
      },
      sortDirections: ['descend', 'ascend'],
      ellipsis: true,
      render: record => {
        return <span className="capitalize">{record.source}</span>;
      }
    }
  ];

  return (
    <Table
      locale={getCustomNoData()}
      columns={columns}
      dataSource={issues}
      rowKey="id"
      pagination={paginationWrapper}
    />
  );
};

LastTrainingTable.propTypes = {
  issues: PropTypes.arrayOf(
    PropTypes.shape({
      definedUtterances: PropTypes.string,
      id: PropTypes.number,
      intent0: PropTypes.string,
      intentId0: PropTypes.string,
      priority: PropTypes.string,
      reason: PropTypes.string,
      source: PropTypes.string,
      type: PropTypes.string,
      utterances: PropTypes.string
    })
  )
};

export { LastTrainingTable };
